import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createPartnerClient,
  deletePartnerClient,
  listPartnerClientDriverSummary,
  listPartnerClientOrders,
  listPartnerClients,
  reissuePartnerClientPasscode,
  updatePartnerClient,
} from "../lib/partnerClients";
import {
  createReadyClient,
  resetClientPassword,
  type CreateReadyClientInput,
} from "../lib/partnerAccounts";
import { usePartnerSessionStore } from "../lib/partnerAuth";

function usePartnerId() {
  return usePartnerSessionStore((s) => s.session?.partnerId ?? null);
}

export function usePartnerClientsList() {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-clients", partnerId],
    enabled: !!partnerId,
    queryFn: () => listPartnerClients(partnerId!),
    refetchInterval: 20_000,
  });
}

export function usePartnerClientOrders(tenantId: string | null) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-client-orders", partnerId, tenantId],
    enabled: !!partnerId && !!tenantId,
    queryFn: () => listPartnerClientOrders(partnerId!, tenantId!),
    refetchInterval: 15_000,
  });
}

export function usePartnerClientDrivers(tenantId: string | null) {
  const partnerId = usePartnerId();
  return useQuery({
    queryKey: ["partner-client-drivers", partnerId, tenantId],
    enabled: !!partnerId && !!tenantId,
    queryFn: () => listPartnerClientDriverSummary(partnerId!, tenantId!),
  });
}

export function useCreatePartnerClient() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      phone: string;
      address: string;
      username: string;
      email?: string;
    }) => {
      if (!partnerId) throw new Error("no_partner");
      return createPartnerClient(partnerId, input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-clients", partnerId] });
    },
  });
}

export function useReissuePartnerClientPasscode() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (tenantId: string) => {
      if (!partnerId) throw new Error("no_partner");
      return reissuePartnerClientPasscode(partnerId, tenantId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-clients", partnerId] });
    },
  });
}

export function useUpdatePartnerClient() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      tenantId: string;
      patch: {
        name?: string;
        phone?: string;
        address?: string;
        username?: string;
        email?: string | null;
      };
    }) => {
      if (!partnerId) throw new Error("no_partner");
      return updatePartnerClient(partnerId, input.tenantId, input.patch);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-clients", partnerId] });
    },
  });
}

export function useDeletePartnerClient() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { tenantId: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return deletePartnerClient(partnerId, input.tenantId);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-clients", partnerId] });
    },
  });
}

/** Ready-to-Use store (276): server creates the Auth login + tenant atomically. */
export function useCreateReadyPartnerClient() {
  const partnerId = usePartnerId();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReadyClientInput) => {
      if (!partnerId) throw new Error("no_partner");
      return createReadyClient(partnerId, input);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["partner-clients", partnerId] });
    },
  });
}

/** Partner-initiated password reset for a linked store owner (276). */
export function useResetPartnerClientPassword() {
  const partnerId = usePartnerId();
  return useMutation({
    mutationFn: (input: { tenantId: string; password?: string }) => {
      if (!partnerId) throw new Error("no_partner");
      return resetClientPassword(partnerId, input.tenantId, input.password);
    },
  });
}
